<template>
  <section id="atmosphere" class="section atmosphere-section">
    <h2 class='section-title'>{{ $t('atmosphere.title') }}</h2>
    <p class='section-subtitle'>{{ $t('atmosphere.subtitle') }}</p>

    <!-- Colours come from the token layer (useThemeTokens.js): the tile glow is
         --shadow-glow (shadow.brandGlow) and the border --color-primary, like
         every other brand.primary border. -->
    <div class="masonry">
      <button
        v-for="(image, index) in images"
        :key="index"
        type="button"
        class="masonry-item"
        @click="openLightbox(index)"
      >
        <img
          :src="image"
          class="masonry-image"
          alt="Atmosphere"
          loading="lazy"
          @error="handleImageError(index, $event)"
        />
      </button>
    </div>

    <!-- Lightbox — teleported to body so position:fixed anchors to the viewport,
         not the transformed .reveal-wrap ancestor -->
    <Teleport to="body">
      <div
        v-if="lightboxOpen"
        class="lightbox"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('common.imageViewer')"
        @click.self="closeLightbox"
      >
        <button type="button" class="lightbox-close" :aria-label="$t('modal.close')" @click="closeLightbox">&times;</button>

        <button
          v-if="images.length > 1"
          type="button"
          class="lightbox-nav lightbox-prev"
          :aria-label="$t('common.previousImage')"
          @click.stop="prevImage"
        >&#8249;</button>

        <img :src="images[lightboxIndex]" class="lightbox-image" alt="Atmosphere" />

        <button
          v-if="images.length > 1"
          type="button"
          class="lightbox-nav lightbox-next"
          :aria-label="$t('common.nextImage')"
          @click.stop="nextImage"
        >&#8250;</button>

        <div v-if="images.length > 1" class="lightbox-counter">{{ lightboxIndex + 1 }} / {{ images.length }}</div>
      </div>
    </Teleport>
  </section>
</template>

<script>
import { useTenantConfig } from '@/composables/useTenantConfig'
import { loadAtmosphereImages } from '@/config/imageConfig'
import { lock, unlock } from '@/composables/useScrollLock'
import { ref, onMounted, onBeforeUnmount } from 'vue'

export default {
  name: 'AtmosphereSection',
  setup() {
    const { config } = useTenantConfig()

    const images = ref([])
    const loading = ref(true)

    // Lightbox state
    const lightboxOpen = ref(false)
    const lightboxIndex = ref(0)

    // Load images
    onMounted(async () => {
      try {
        images.value = await loadAtmosphereImages(config.value?.images)
        console.log(`Loaded ${images.value.length} atmosphere images`)
      } catch (error) {
        console.error('Failed to load atmosphere images:', error)
      } finally {
        loading.value = false
      }
      window.addEventListener('keydown', handleKeydown)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleKeydown)
      if (lightboxOpen.value) {
        lightboxOpen.value = false
        unlock()
      }
    })

    // Handle image errors
    const handleImageError = (index) => {
      console.warn(`Failed to load image at index ${index}`)
      images.value.splice(index, 1)
    }

    // Lightbox controls
    const openLightbox = (index) => {
      lightboxIndex.value = index
      lightboxOpen.value = true
      lock()
    }

    const closeLightbox = () => {
      if (!lightboxOpen.value) return
      lightboxOpen.value = false
      unlock()
    }

    const nextImage = () => {
      if (images.value.length === 0) return
      lightboxIndex.value = (lightboxIndex.value + 1) % images.value.length
    }

    const prevImage = () => {
      if (images.value.length === 0) return
      lightboxIndex.value = (lightboxIndex.value - 1 + images.value.length) % images.value.length
    }

    const handleKeydown = (e) => {
      if (!lightboxOpen.value) return
      if (e.key === 'Escape') closeLightbox()
      else if (e.key === 'ArrowRight') nextImage()
      else if (e.key === 'ArrowLeft') prevImage()
    }

    return {
      images,
      loading,
      handleImageError,
      lightboxOpen,
      lightboxIndex,
      openLightbox,
      closeLightbox,
      nextImage,
      prevImage
    }
  }
}
</script>

<style scoped>
.atmosphere-section {
  padding: 4rem 2rem;
}

.section-title {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--color-heading);
  text-shadow: 1px 1px 3px var(--text-shadow-color);
}

.section-subtitle {
  font-size: 1.2rem;
  color: var(--text-strong);
  text-shadow: 1px 1px 3px var(--text-shadow-color);
}

/* Masonry via CSS columns — varied heights, no gaps for odd counts */
.masonry {
  margin-top: 3rem;
  column-count: 3;
  column-gap: 1rem;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
}

.masonry-item {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  break-inside: avoid;
  margin-bottom: 1rem;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--shadow-glow);
  border: 3px solid var(--color-primary);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.masonry-item:hover,
.masonry-item:focus-visible {
  transform: translateY(-4px);
  outline: none;
}

.masonry-image {
  display: block;
  width: 100%;
  height: auto;
}

/* Lightbox */
.lightbox {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.88);
  padding: 2rem;
}

.lightbox-image {
  max-width: 90vw;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
}

.lightbox-close {
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  font-size: 2.5rem;
  line-height: 1;
  color: #fff;
  background: none;
  border: none;
  cursor: pointer;
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 3rem;
  line-height: 1;
  color: #fff;
  background: rgba(0, 0, 0, 0.3);
  border: none;
  cursor: pointer;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-nav:hover {
  background: rgba(0, 0, 0, 0.6);
}

.lightbox-prev { left: 1rem; }
.lightbox-next { right: 1rem; }

.lightbox-counter {
  position: absolute;
  bottom: 1.25rem;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 0.95rem;
  letter-spacing: 0.05em;
}

@media (max-width: 900px) {
  .masonry {
    column-count: 2;
  }
}

@media (max-width: 500px) {
  .masonry {
    column-count: 1;
    max-width: 360px;
  }

  .lightbox-nav {
    width: 2.75rem;
    height: 2.75rem;
    font-size: 2.25rem;
  }
}

/* Desktop polish layer — additive only, does not touch base/mobile styles */
@media (min-width: 1024px) {
  .section-title {
    font-size: 3rem;
  }

  .masonry {
    max-width: 1200px;
  }
}

@media (min-width: 1280px) {
  .masonry {
    column-count: 4;
  }
}
</style>
